import { serieMatchCourt, timeToMinutes } from './time.js';

// ─────────────────────────────────────────────────────────────────────
// resolveBanepadCourtContext — the pure per-court "what should this pad show"
// resolver, mirroring BanepadCentralView's `courtCtx` (manual force > auto-force
// window > normal). Extracted so the physical Banepad device can show a court
// EXACTLY like Sentralen, including a manually forced session and the
// auto-force-around-booking window — not just the bare clock-based resolution.
//
// Priority (identical to courtCtx):
//   1. manual force (forcedTournamentId points at a tournament OR the series),
//   2. all-enkeltmatch force (the SM_FORCE_ALL sentinel),
//   3. auto-force window (a booking-linked event live within ± its buffer),
//   4. normal (the active tournament/series, resolved by the clock downstream).
//
// PURE: no Storage, no React. The caller supplies the persisted force state
// (forcedTournamentId, autoForce) + the live data + now.
//
// TYPING (#192): the `any`s this file carried out of the monolith are gone. The
// data it reads is genuinely loose — tournaments, the series, bookings,
// activities and planned matches all arrive as parsed JSON, from the browser
// store or from the API's bridge rows and promoted tables — so the shapes below
// describe only the fields THIS resolver reads, with every value `unknown` and
// an open tail for the rest of the record. That is deliberate: a field typed
// `string` here would be a claim about persisted data that nothing validates,
// while `unknown` makes each read state its own assumption instead of letting
// the whole object be anything. The few casts in the body are exactly those
// assumptions; they erase at runtime, so the emitted code is unchanged.
//
// THE TIMING LOGIC IS NOW SHARED (#663): `BanepadCentralView` calls
// `resolveBanepadAutoForce` rather than keeping its own copy of the window maths,
// which was the half a divergence could hide in. Its `courtCtx` still assembles the
// decision tree locally — identical to the tail of `resolveBanepadCourtContext`,
// verified line by line — because delegating that too means threading every dep
// through the large central component, and it is the harmless half.
// ─────────────────────────────────────────────────────────────────────

/** Sentinel forcedTournamentId meaning "force every court's own enkeltmatch". */
export const BANEPAD_SM_FORCE_ALL = '__ALL_SINGLE_MATCH__';

export interface BanepadAutoForceConfig {
  enabled?: boolean;
  minutesBefore?: number;
  minutesAfter?: number;
  afterOnlyIfPending?: boolean;
}

/**
 * A match on a session, as far as THIS resolver is concerned: which court it is
 * on (directly or via its reservation) and whether it still needs a result.
 */
export interface BanepadMatchLike {
  court?: unknown;
  /** A series match records its court on the reservation instead. */
  bookingInfo?: { court?: unknown } | null;
  score?: unknown;
  completed?: unknown;
  [key: string]: unknown;
}

/** A session's `config` blob — only the pad's own override is read here. */
export interface BanepadSessionConfigLike {
  banepadAutoForce?: BanepadAutoForceConfig;
  [key: string]: unknown;
}

/**
 * A tournament or the active series. Both are matched by id, both can carry a
 * per-session auto-force override, and both are handed straight back to the
 * caller — which is why one shape covers the two.
 */
export interface BanepadSessionLike {
  id?: unknown;
  /** Some sessions are identified by a human-facing id instead. */
  displayId?: unknown;
  /** Guarded with Array.isArray before use — an event without matches is skipped. */
  matches?: unknown;
  config?: unknown;
  [key: string]: unknown;
}

/** A court reservation: the link between a court's clock and a session. */
export interface BanepadBookingLike {
  id?: unknown;
  court?: unknown;
  date?: unknown;
  /** 'HH:MM'. */
  startTime?: unknown;
  /** 'HH:MM'. Missing, or not after the start, means "runs past midnight". */
  endTime?: unknown;
  /** The session (tournament, series or activity) this reservation belongs to. */
  seriesId?: unknown;
  [key: string]: unknown;
}

/** An activity's `config` blob — only the format is read here. */
export interface BanepadActivityConfigLike {
  tournamentType?: unknown;
  [key: string]: unknown;
}

/** A club activity — an enkeltmatch when its config says so. */
export interface BanepadActivityLike {
  id?: unknown;
  status?: unknown;
  config?: unknown;
  [key: string]: unknown;
}

/** A locally planned enkeltmatch, attached to a booking rather than an activity. */
export interface BanepadPlannedMatchLike {
  bookingId?: unknown;
  status?: unknown;
  [key: string]: unknown;
}

/** The enkeltmatch a court can be forced to: an activity OR a local plan. */
export type BanepadSingleMatchLike = BanepadActivityLike | BanepadPlannedMatchLike;

export interface BanepadCourtContext {
  tournament: BanepadSessionLike | null;
  activeSeries: BanepadSessionLike | null;
  forceTournament: boolean;
  forceSeries: boolean;
  forceSingleMatch: BanepadSingleMatchLike | null;
}

export interface ResolveBanepadCourtContextDeps {
  court: number | string;
  tournament: BanepadSessionLike | null;
  pausedTournaments?: BanepadSessionLike[];
  activeSeries: BanepadSessionLike | null;
  bookings?: BanepadBookingLike[];
  activities?: BanepadActivityLike[];
  plannedMatches?: BanepadPlannedMatchLike[];
  /**
   * Persisted manual force target (banepad_forced_tourn_v1) — a tournament/series
   * id or the SM sentinel. `unknown` because the id space is not one type: legacy
   * ids are numbers and current ones strings, and this value is only ever
   * compared, never parsed.
   */
  forcedTournamentId?: unknown;
  /** Persisted auto-force config (banepad_autoforce_v1). */
  autoForce?: BanepadAutoForceConfig | null;
  nowMin: number;
  todayISO: string;
}

/** One auto-force window: which event owns this court now, and until when. */
export interface BanepadAutoForceWindow {
  event: BanepadSessionLike;
  isSeries: boolean;
  /** Wrapped minutes, buffer included. The operator header reads this. */
  windowEnd: number;
}

export interface ResolveBanepadAutoForceDeps {
  court: number | string;
  tournament: BanepadSessionLike | null;
  pausedTournaments?: BanepadSessionLike[];
  activeSeries: BanepadSessionLike | null;
  bookings?: BanepadBookingLike[];
  autoForce?: BanepadAutoForceConfig | null;
  nowMin: number;
  todayISO: string;
}

// ═══════════════════════════════════════════════════════════════════
// WHICH EVENT'S TIME WINDOW CONTAINS «NOW» ON THIS COURT (#663).
//
// Extracted so the device's context resolver and the OPERATOR VIEW
// (`BanepadCentralView`) share one copy. They had identical copies, verified line
// by line — and this is the half where a divergence would actually hide: the
// booking window, the per-session buffer override, `afterOnlyIfPending`, and the
// midnight wrap. The duplicated decision tree around it is the harmless half.
//
// ── WHY THE SUPPRESSION IS NOT IN HERE ──────────────────────────────
//
// «A manual force overrides auto» is the CALLER's rule: the resolver knows what a
// forced tournament id means, and the operator header suppresses on the same
// condition before building its list. Folding it in would mean this function
// deciding something both callers already decided, from inputs one of them does
// not have.
//
// ── THE WINDOW IS HALF-OPEN, AND WRAPS ──────────────────────────────
//
// `nowMin >= s - before && nowMin < e + after`: the first minute is inside and the
// last is not, the same convention the pairing code's expiry uses. A booking whose
// end is not after its start is a booking that crosses midnight, so the end gains
// a day — without that its window is negative and contains nothing. Both pinned by
// `banepad-court-context.test.ts`.
// ═══════════════════════════════════════════════════════════════════
export function resolveBanepadAutoForce({
  court,
  tournament,
  pausedTournaments = [],
  activeSeries,
  bookings = [],
  autoForce,
  nowMin,
  todayISO,
}: ResolveBanepadAutoForceDeps): BanepadAutoForceWindow | null {
  if (!autoForce?.enabled) return null;
  const eventList = [tournament, ...(pausedTournaments || [])].filter(Boolean);
  const cbs = (bookings || []).filter(
    (b) => Number(b.court) === Number(court) && b.date === todayISO && b.seriesId,
  );
  for (const b of cbs) {
    // Every booking the club writes carries 'HH:MM' strings; the cast says so
    // here rather than widening timeToMinutes, which is shared engine surface.
    const s = timeToMinutes((b.startTime as string) || '00:00');
    let e = timeToMinutes((b.endTime as string) || (b.startTime as string) || '00:00');
    if (e <= s) e += 1440;
    const tourn = eventList.find((t) => (t.id || t.displayId) === b.seriesId);
    const isSer = !!(activeSeries && (activeSeries.id || activeSeries.displayId) === b.seriesId);
    const event = tourn || (isSer ? activeSeries : null);
    if (!event || !Array.isArray(event.matches)) continue;
    const cfg = {
      ...autoForce,
      ...((event.config as BanepadSessionConfigLike)?.banepadAutoForce || {}),
    };
    const before = Number(cfg.minutesBefore) || 0;
    let after = Number(cfg.minutesAfter) || 0;
    if (cfg.afterOnlyIfPending) {
      const cm = (event.matches || []).filter(
        (m: BanepadMatchLike) => Number(serieMatchCourt(m)) === Number(court),
      );
      const pending = cm.some((m: BanepadMatchLike) => !(m.score || m.completed));
      if (!pending) after = 0; // all results in → don't hold the pad any longer
    }
    if (nowMin >= s - before && nowMin < e + after) {
      return { event, isSeries: isSer, windowEnd: e + after };
    }
  }
  return null;
}

export function resolveBanepadCourtContext({
  court,
  tournament,
  pausedTournaments = [],
  activeSeries,
  bookings = [],
  activities = [],
  plannedMatches = [],
  forcedTournamentId,
  autoForce,
  nowMin,
  todayISO,
}: ResolveBanepadCourtContextDeps): BanepadCourtContext {
  const all = [tournament, ...(pausedTournaments || [])].filter(Boolean);
  const forcedTournament = forcedTournamentId
    ? all.find((t) => (t.id || t.displayId) === forcedTournamentId) || null
    : null;
  const effectiveTournament = forcedTournament || tournament;
  const forcedSeries =
    activeSeries &&
    forcedTournamentId &&
    (activeSeries.id || activeSeries.displayId) === forcedTournamentId
      ? activeSeries
      : null;
  const allSingleMatchForced = forcedTournamentId === BANEPAD_SM_FORCE_ALL;

  // The court's enkeltmatch (activity via booking.seriesId, or local plan via
  // plan.bookingId) when all-enkeltmatch is forced.
  const singleMatchForCourt = (): BanepadSingleMatchLike | null => {
    if (!allSingleMatchForced) return null;
    const todays = (bookings || []).filter(
      (b) => Number(b.court) === Number(court) && b.date === todayISO,
    );
    for (const b of todays) {
      if (b.seriesId) {
        const act = (activities || []).find(
          (a) =>
            a.id === b.seriesId &&
            (a.config as BanepadActivityConfigLike)?.tournamentType === 'single_match' &&
            a.status !== 'cancelled' &&
            a.status !== 'completed',
        );
        if (act) return act;
      }
      const plan = (plannedMatches || []).find(
        (p) => p.bookingId === b.id && p.status !== 'cancelled' && p.status !== 'completed',
      );
      if (plan) return plan;
    }
    return null;
  };

  // The event that should be auto-forced on this court right now (or null).
  //
  // THE WINDOW MATHS IS SHARED (#663) — see `resolveBanepadAutoForce`. Only the
  // suppression stays here, because what counts as «manually forced» is this
  // function's own business.
  const autoForceForCourt = () => {
    if (forcedTournament || forcedSeries || allSingleMatchForced) return null; // manual force overrides auto
    return resolveBanepadAutoForce({
      court,
      tournament,
      pausedTournaments,
      activeSeries,
      bookings,
      autoForce,
      nowMin,
      todayISO,
    });
  };

  // Effective pad context per court: manual force > auto-force window > normal.
  if (forcedTournament || forcedSeries) {
    return {
      tournament: effectiveTournament,
      activeSeries,
      forceTournament: !!forcedTournament,
      forceSeries: !!forcedSeries,
      forceSingleMatch: null,
    };
  }
  if (allSingleMatchForced) {
    return {
      tournament: effectiveTournament,
      activeSeries,
      forceTournament: false,
      forceSeries: false,
      forceSingleMatch: singleMatchForCourt(),
    };
  }
  const af = autoForceForCourt();
  if (af) {
    if (af.isSeries)
      return {
        tournament: null,
        activeSeries: af.event,
        forceTournament: false,
        forceSeries: true,
        forceSingleMatch: null,
      };
    return {
      tournament: af.event,
      activeSeries: null,
      forceTournament: true,
      forceSeries: false,
      forceSingleMatch: null,
    };
  }
  return {
    tournament: effectiveTournament,
    activeSeries,
    forceTournament: false,
    forceSeries: false,
    forceSingleMatch: null,
  };
}
